/**
 * Labelled form controls (task 9.2, 9.8).
 *
 * Every control here is a real one — a `<select>`, a checkbox — with a real
 * `<label>` bound to it. The Playwright agent finds these by accessible name,
 * and a custom listbox that merely looks like a select would be a control it
 * cannot see.
 */
import { useId, type JSX, type ReactNode, type SelectHTMLAttributes } from 'react';

export function Field({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly children: (ids: { readonly controlId: string; readonly hintId: string }) => ReactNode;
}): JSX.Element {
  const controlId = useId();
  const hintId = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={controlId} className="text-sm font-medium text-ink">
        {label}
      </label>
      {children({ controlId, hintId })}
      {hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface SelectFieldProps<T extends string> extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'value'
> {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly value: T;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  onChange(value: T): void;
}

export function SelectField<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
  ...rest
}: SelectFieldProps<T>): JSX.Element {
  return (
    <Field label={label} hint={hint}>
      {({ controlId, hintId }) => (
        <select
          id={controlId}
          value={value}
          aria-describedby={hint ? hintId : undefined}
          onChange={(event) => {
            onChange(event.target.value as T);
          }}
          className="touch-target rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm text-ink"
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function CheckboxField({
  label,
  hint,
  checked,
  onChange,
  testId,
}: {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly checked: boolean;
  readonly testId?: string | undefined;
  onChange(checked: boolean): void;
}): JSX.Element {
  const controlId = useId();
  const hintId = useId();
  return (
    <div className="flex flex-col gap-1.5">
      {/*
        The label is the target, not the 20px box (task 19.1).

        A native checkbox drawn at 44x44 looks like a shipping form, so the box
        stays 20px and the *label* — which toggles it, because it is bound by
        `htmlFor` — carries the 44px minimum and the row's whole width. The
        audit in `tests/e2e/support/audit.ts` unions an input's box with its
        label's for exactly this reason, so what it measures is what a finger
        can land on.
      */}
      <div className="flex items-center">
        <input
          id={controlId}
          type="checkbox"
          checked={checked}
          data-testid={testId}
          aria-describedby={hint ? hintId : undefined}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
          className="size-5 shrink-0 accent-[var(--mu-accent)]"
        />
        <label
          htmlFor={controlId}
          className="touch-target flex flex-1 cursor-pointer items-center pl-3 text-sm font-medium text-ink"
        >
          {label}
        </label>
      </div>
      {hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  testId,
  placeholder,
}: {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly value: string;
  readonly testId?: string | undefined;
  readonly placeholder?: string | undefined;
  onChange(value: string): void;
}): JSX.Element {
  return (
    <Field label={label} hint={hint}>
      {({ controlId, hintId }) => (
        <input
          id={controlId}
          type="text"
          value={value}
          placeholder={placeholder}
          data-testid={testId}
          aria-describedby={hint ? hintId : undefined}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className="touch-target rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm text-ink"
        />
      )}
    </Field>
  );
}
